export const COOKIE_NAME = "toyo_session";
export const APP_NAME = "DreamTour";
export const SUPPORTED_LANGUAGES = ["zh-TW", "en-US", "zh-CN"] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = "zh-TW";
/** 多語字串（zh-TW / en-US / zh-CN） */
export type Loc = Record<Language, string>;

export const ROLES = ["admin", "dispatcher", "agency", "fleet"] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LEVEL: Record<Role, number> = {
  admin: 80,
  dispatcher: 60,
  fleet: 40,
  agency: 30,
};

export const REGIONS = ["taiwan", "hongkong"] as const;
export type Region = (typeof REGIONS)[number];

/** 需求方地區 → 預設國碼（D7 A6：乘客電話國碼下拉預設值） */
export const REGION_DIAL_CODES: Record<Region, string> = { taiwan: "+886", hongkong: "+852" };
/** 乘客電話國碼下拉選項（台港優先，含常見亞洲/歐美） */
export const DIAL_CODES = ["+852", "+886", "+86", "+81", "+82", "+65", "+60", "+66", "+1", "+44"] as const;

export const CURRENCIES = ["TWD", "HKD", "USD"] as const;
export type Currency = (typeof CURRENCIES)[number];

export const ORDER_STATUSES = [
  "pending",
  "quoting",
  "quoted",
  "rejected_quote",
  "confirmed",
  "dispatching",
  "rejected_dispatch",
  "active",
  "completed",
  "cancelled",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** 待派遣（需調度員指派車隊）：已確認報價、或車隊婉拒待重派。
 *  單一事實來源 — 工作台計數與派遣作業列表共用，避免口徑分歧（P4-1）。 */
export const PENDING_DISPATCH_STATUSES = ["confirmed", "rejected_dispatch"] as const satisfies readonly OrderStatus[];

/** 待報價（需調度員報價）：新單、報價中、被退回重議。 */
export const PENDING_QUOTE_STATUSES = ["pending", "quoting", "rejected_quote"] as const satisfies readonly OrderStatus[];

/** 服務類型：機場接機 / 機場送機 / 來回接送（legacy 顯示用）/ 點對點接送 / 包車。 */
export const SERVICE_TYPES = ["airport_pickup", "airport_dropoff", "round_trip", "point_to_point", "charter"] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];

/** 服務類型顯示與行為：needsFlight 為真時，建單顯示航班/機場欄；legacy 為真時不可新建（僅舊單顯示）。 */
export const SERVICE_TYPE_META: Record<ServiceType, { label: Loc; needsFlight: boolean; legacy?: boolean }> = {
  airport_pickup: { label: { "zh-TW": "機場接機", "en-US": "Airport pickup", "zh-CN": "机场接机" }, needsFlight: true },
  airport_dropoff: { label: { "zh-TW": "機場送機", "en-US": "Airport drop-off", "zh-CN": "机场送机" }, needsFlight: true },
  round_trip: { label: { "zh-TW": "來回接送", "en-US": "Round trip", "zh-CN": "来回接送" }, needsFlight: true, legacy: true },
  point_to_point: { label: { "zh-TW": "點對點接送", "en-US": "Point to point", "zh-CN": "点对点接送" }, needsFlight: false },
  charter: { label: { "zh-TW": "包車", "en-US": "Charter", "zh-CN": "包车" }, needsFlight: false },
};

/** 車型與容量上限（建單時驗證人數/行李，避免選錯車型）。數值可依實務調整。 */
export const VEHICLE_TYPES = [
  { value: "sedan", maxPax: 4, maxLuggage: 3, label: { "zh-TW": "轎車", "en-US": "Sedan", "zh-CN": "轿车" } },
  { value: "suv", maxPax: 6, maxLuggage: 4, label: { "zh-TW": "休旅車 SUV", "en-US": "SUV", "zh-CN": "休旅车 SUV" } },
  { value: "van", maxPax: 9, maxLuggage: 8, label: { "zh-TW": "商務車 / Van", "en-US": "Van", "zh-CN": "商务车 / Van" } },
  { value: "bus", maxPax: 20, maxLuggage: 20, label: { "zh-TW": "大巴 / Bus", "en-US": "Bus", "zh-CN": "大巴 / Bus" } },
] as const satisfies readonly { value: string; maxPax: number; maxLuggage: number; label: Loc }[];

/**
 * 車型照片的顯示比例：2.5:1（Tailwind 寫成 aspect-[5/2]）。
 *
 * 四個顯示點都鎖同一比例，業主只要準備一種圖：建單選車卡、訂單詳情縮圖、
 * 後台車款列表縮圖、車款編輯的預覽。四處全用 object-cover（置中裁切），
 * 比例不一致的話同一張圖在每個地方會被裁掉不同部位。
 *
 * 為什麼是 2.5:1 而不是相機原生的 16:9：選車卡在桌機是兩欄、每張卡約 353px 寬，
 * 換成 16:9 圖高會到 197px，建單步驟 3 從「一屏看完」變成要捲動才找得到「下一步」，
 * 而且車身只佔畫面中間三分之一、四周大片留白（2026-09-21 實測）。
 * 2.5:1 的圖高 141px，2×2 排版與首屏都保得住，車體也能佔滿畫面。
 *
 * 這個常數本身不會被 CSS 讀到（Tailwind 需要靜態 class），改比例時四處的
 * aspect-[5/2] 要一起換，並同步更新下面給業主看的 VEHICLE_PHOTO_HINT。
 */
export const VEHICLE_PHOTO_RATIO = 2.5;

/** 車款編輯頁「照片 URL」欄位下的規格提示，直接講給維護車款的人看。 */
export const VEHICLE_PHOTO_HINT: Loc = {
  "zh-TW": "建議 1500 × 600（2.5:1），車體置中、左右各留約 5%。畫面會置中裁切，過高的圖會被切掉上下。",
  "en-US": "Recommended 1500 × 600 (2.5:1), vehicle centred with ~5% margin on each side. Images are centre-cropped, so taller photos lose their top and bottom.",
  "zh-CN": "建议 1500 × 600（2.5:1），车体置中、左右各留约 5%。画面会置中裁切，过高的图会被切掉上下。",
};

/** 上/下車地點 autofill 建議清單（非限定，使用者仍可自由輸入）。 */
export const COMMON_LOCATIONS = [
  "台北車站",
  "台北 101",
  "香港尖沙咀",
  "香港中環",
] as const;

/** 內建機場資訊（名稱＋IATA 代碼＋航廈）— 建單地點可預帶，使用者亦可改自由輸入。 */
export const AIRPORTS = [
  { code: "TPE", region: "taiwan", name: { "zh-TW": "台灣桃園國際機場", "en-US": "Taoyuan Intl", "zh-CN": "台湾桃园国际机场" }, terminals: ["T1", "T2"] },
  { code: "TSA", region: "taiwan", name: { "zh-TW": "台北松山機場", "en-US": "Taipei Songshan", "zh-CN": "台北松山机场" }, terminals: [] },
  { code: "KHH", region: "taiwan", name: { "zh-TW": "高雄國際機場", "en-US": "Kaohsiung Intl", "zh-CN": "高雄国际机场" }, terminals: [] },
  { code: "HKG", region: "hongkong", name: { "zh-TW": "香港國際機場", "en-US": "Hong Kong Intl", "zh-CN": "香港国际机场" }, terminals: ["T1"] },
] as const satisfies readonly { code: string; region: Region; name: Loc; terminals: readonly string[] }[];

/** 車費成本的固定金額加購項（如安全座椅）。amount 為當地幣別固定金額。 */
export type Surcharge = { label: string; amount: number };

/** 加購項預設建議清單（金額由 admin 填）。 */
export const SURCHARGE_PRESETS: Loc[] = [
  { "zh-TW": "安全座椅", "en-US": "Child safety seat", "zh-CN": "安全座椅" },
  { "zh-TW": "兒童座椅", "en-US": "Booster seat", "zh-CN": "儿童座椅" },
  { "zh-TW": "額外停靠", "en-US": "Extra stop", "zh-CN": "额外停靠" },
  { "zh-TW": "等候費", "en-US": "Waiting fee", "zh-CN": "等候费" },
];

export const ERR = {
  UNAUTHED: "請先登入",
  FORBIDDEN: "權限不足",
  NOT_FOUND: "找不到資源",
  INVALID_INPUT: "輸入資料有誤",
  EMAIL_TAKEN: "此 Email 已被註冊",
  INVALID_CREDENTIALS: "帳號或密碼錯誤",
  ACCOUNT_INACTIVE: "帳號未啟用",
  // 帳號管理（2026-08-21）
  PASSWORD_CHANGE_REQUIRED: "請先變更密碼後再繼續使用",
  CURRENT_PASSWORD_WRONG: "目前密碼不正確",
  PASSWORD_SAME_AS_OLD: "新密碼不可與目前密碼相同",
  PASSWORD_TOO_WEAK: "密碼須為 8–128 字，且至少包含一個英文字母與一個數字",
  RESET_TOKEN_INVALID: "重設連結無效或已過期，請重新申請",
  OWNER_PROTECTED: "不可停用或降級系統擁有者",
  SELF_PROTECTED: "不可停用或降級自己的帳號",
  LAST_ADMIN: "系統至少需保留一位啟用中的管理員",
  ROLE_CHANGE_NOT_ALLOWED: "僅後台角色（管理員／調度員）可互換；需求方／供應方角色不可變更",
  PARTY_FIELDS_REQUIRED: "需求方／供應方帳號須填寫公司名稱與區域",
  ROLE_MANAGE_FORBIDDEN: "你的角色無權管理此帳號",
} as const;
